/* eslint-disable no-console */

import React, {useEffect} from 'react';
import Head from 'next/head';
import type {AppProps} from 'next/app';
import {useRouter} from 'next/router';
import type {SSRConfig, UserConfig} from 'next-i18next';
import {appWithTranslation} from 'next-i18next';
import configNextI18Next from '../next-i18next.config';
import {initLogCenter} from '@deezer/next/services/tracking/LogCenter';

import './global.scss';

import AppProviders from '@deezer/next/shared/providers/AppProviders';
import type {
	NextPageWithLayout,
	PageProps,
} from '@deezer/next/services/types/next';

// Stylesheets
import '@deezer/next/shared/styles/sass/bootstrap.scss';
import '@deezer/next/shared/styles/sass/starmania.scss';
import RouteGuard from '../src/shared/router/RouteGuard';
import {initDateLocale} from '@deezer/next/shared/utils/init';
import ErrorPage from '@deezer/next/features/ErrorPage';
import {Routes} from '@constants/routes';
import {STARMANIA_AUTH_TOKEN_STORAGE_KEY} from '@constants/storageKeys';
import {initGlobal} from '@deezer/next/shared/utils/init';
import {ErrorBoundary} from '@components/ErrorBoundary';

type AppPropsWithLayout = AppProps<PageProps & SSRConfig> & {
	Component: NextPageWithLayout;
};

initDateLocale();

initLogCenter();

initGlobal();

const App = ({Component, pageProps}: AppPropsWithLayout) => {
	const router = useRouter();
	const {locale, locales, asPath} = router;
	const getLayout = Component.getLayout ?? (page => page);

	useEffect(() => {
		if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
			return;
		}

		if (process.env.NODE_ENV !== 'production') {
			navigator.serviceWorker.getRegistrations().then(registrations => {
				registrations.forEach(registration => {
					void registration.unregister();
				});
			});

			return;
		}

		void navigator.serviceWorker.register('/sw.js').catch(err => {
			console.error('Service worker registration failed:', err);
		});
	}, []);

	useEffect(() => {
		const handleStorageChange = async (event: StorageEvent) => {
			if (
				event.key === STARMANIA_AUTH_TOKEN_STORAGE_KEY &&
				event.newValue === null
			) {
				await router.push(Routes.HOME);
			}
		};

		if (typeof window !== 'undefined') {
			window.addEventListener('storage', handleStorageChange);
		}

		return () => {
			if (typeof window !== 'undefined') {
				window.removeEventListener('storage', handleStorageChange);
			}
		};
	}, [router]);

	return (
		<>
			<Head>
				<>
					{(locales || [])
						.filter(l => l !== locale)
						.map(currentLocale => (
							<link
								key={currentLocale}
								rel="alternate"
								hrefLang={currentLocale}
								href={`/${currentLocale}${asPath}`}
							/>
						))}
				</>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
				/>
				<title>Deezer for Creators</title>
			</Head>

			<AppProviders mode={pageProps.mode || 'light'}>
				<ErrorBoundary fallback={<ErrorPage type="client" />}>
					<RouteGuard
						unlogged={pageProps.unlogged}
						logged={pageProps.logged}
						right={pageProps.right}
						userTypeRestriction={pageProps.userTypeRestriction}
					>
						{getLayout(<Component {...pageProps} />)}
					</RouteGuard>
				</ErrorBoundary>
			</AppProviders>

			<div id="modal-root" />
			<div id="banner-root" />
		</>
	);
};

export default appWithTranslation<AppPropsWithLayout>(
	App,
	configNextI18Next as UserConfig,
);
